import React, { useRef, useState } from 'react';
import { mergeProps, useLongPress, usePress } from 'react-aria';

import ColorSwatchIcon from '../../../../bom/components/ColorSwatchIcon';
import LocalImageIcon from '../../../../bom/components/LocalImageIcon';
import PropTypes from 'prop-types';
import Scene7ImageIcon from '../../../../bom/components/Scene7ImageIcon';
import { ThumbnailType } from '../../../../bom/constants';
import TooltipWrapper from '../../../common/TooltipWrapper';
import { isMobile } from 'react-device-detect';
import styles from './GraphicOptionListbox.scss';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useOnInView } from 'react-intersection-observer';

const GraphicOptionListboxItem = React.forwardRef(
  ({ id, itemData, itemIndex, showTooltip, onClickItem, onHoverOutItem, onHoverOverItem }, listboxItemRef) => {
    const { focused, graphicType, hovered, option, previewable } = itemData;
    const {
      codeValue,
      colorReference,
      isOutOfStock,
      isSelected,
      isSoldOut,
      optionText,
      outOfStockMessage,
      questionId,
    } = option;

    let label = optionText;
    if (isOutOfStock) label += `. ${!!outOfStockMessage ? outOfStockMessage : 'Temporarily out of stock.'}`;
    if (isSoldOut) label += ', sold out';

    let className = styles['option'];
    if (isOutOfStock) className += ` ${styles['out-of-stock']}`;
    if (isSoldOut) className += ` ${styles['sold-out']}`;
    if (hovered) className += ` ${styles['hovered']}`;
    if (focused) className += ` ${styles['focused']}`;
    if (isSelected) className += ` ${styles['selected']}`;

    const { longPressProps } = useLongPress({
      accessibilityDescription: 'Long press to preview available options',
      isDisabled: !isMobile || !previewable,
      onLongPressStart: (e) => {
        console.log(`long press start with ${e.pointerType}`);
      },
      onLongPressEnd: (e) => {
        console.log(`long press end with ${e.pointerType}`);
        onHoverOutItem();
      },
      onLongPress: (e) => {
        console.log(`long press with ${e.pointerType}`);
        onHoverOverItem(itemIndex);
      },
    });

    const { pressProps } = usePress({
      onPress: (e) => {
        console.log(`press with ${e.pointerType}`);
        if (isSoldOut) return;
        onClickItem(itemIndex);
      },
    });

    const pressInteractions = isMobile ? mergeProps(pressProps, longPressProps) : pressProps;

    return (
      <TooltipWrapper
        id={`${id}-tooltip`}
        message={label}
        trigger={isMobile ? undefined : ['focus', 'hover']}
        show={isMobile ? showTooltip : undefined}>
        {({ onMouseOver, ref, ...restPropsFromTooltip }) => (
          <div
            aria-disabled={isSoldOut}
            aria-label={label}
            aria-selected={isSelected}
            className={className}
            id={id}
            ref={(node) => {
              if (!!listboxItemRef) {
                listboxItemRef(node);
              }
              ref(node);
            }}
            role="option"
            {...restPropsFromTooltip}
            {...pressInteractions}
            onMouseLeave={onHoverOutItem}
            onMouseOver={(e) => {
              onMouseOver(e);
              onHoverOverItem(itemIndex);
            }}>
            {graphicType === ThumbnailType.LOCAL_IMAGE ? (
              <LocalImageIcon imageName={codeValue} size="sm" />
            ) : graphicType === ThumbnailType.SCENE7_IMAGE ? (
              <Scene7ImageIcon option={option} questionId={questionId} />
            ) : (
              <ColorSwatchIcon active={isSelected} color={colorReference} showCheckIcon size="sm" />
            )}
          </div>
        )}
      </TooltipWrapper>
    );
  }
);

GraphicOptionListboxItem.propTypes = {
  id: PropTypes.string.isRequired,
  itemData: PropTypes.shape({
    focused: PropTypes.bool.isRequired,
    graphicType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    hovered: PropTypes.bool.isRequired,
    option: PropTypes.shape({
      code: PropTypes.string.isRequired,
      codeValue: PropTypes.string.isRequired,
      colorGroupList: PropTypes.arrayOf(PropTypes.string).isRequired,
      colorReference: PropTypes.string.isRequired,
      componentId: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      estimatedShipDate: PropTypes.string.isRequired,
      futureShipDateSettingStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
      helpLink: PropTypes.shape({
        helpText: PropTypes.string.isRequired,
        linkText: PropTypes.string.isRequired,
      }),
      inventoryMessage: PropTypes.string.isRequired,
      isFutureShipDateSettable: PropTypes.bool.isRequired,
      isOutOfStock: PropTypes.bool.isRequired,
      isSelected: PropTypes.bool.isRequired,
      isSoldOut: PropTypes.bool.isRequired,
      itemId: PropTypes.string.isRequired,
      optionText: PropTypes.string.isRequired,
      outOfStockMessage: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      questionId: PropTypes.number.isRequired,
      sequence: PropTypes.number.isRequired,
      specialBomCharge: PropTypes.string.isRequired,
      useTexture: PropTypes.bool.isRequired,
      yearOptionPricing: PropTypes.oneOf([0, 1, 2]).isRequired,
    }).isRequired,
    previewable: PropTypes.bool.isRequired,
  }).isRequired,
  itemIndex: PropTypes.number.isRequired,
  showTooltip: PropTypes.bool,
  onClickItem: PropTypes.func,
  onHoverOutItem: PropTypes.func.isRequired,
  onHoverOverItem: PropTypes.func.isRequired,
};

const GraphicOptionListbox = ({
  graphicType,
  id,
  isInvalid = false,
  multiSelectable = false,
  options,
  previewable = false,
  scrollable = false,
  scrollingIntoView = false,
  showPreviewWhenHoveredOutSwatch = false,
  onPreviewOption,
  onSelectOption,
  ...rest
}) => {
  const keys = options.map((option) => `${option.code}-${option.codeValue}`);

  const ref = useRef(null);
  const timerRef = useRef(null);

  const [focused, setFocused] = useState(-1);
  const [hovered, setHovered] = useState(-1);
  const [tooltipShowingStatus, setTooltipShowingStatus] = useState(Object.fromEntries(keys.map((key) => [key, false])));

  useDeepCompareEffect(() => {
    setTooltipShowingStatus(Object.fromEntries(keys.map((key) => [key, false])));
  }, [keys]);

  const trackingRef = useOnInView((inView, entry) => {
    if (inView) {
      console.log('The tracking swatch appeared in view!', entry.target);
    } else {
      console.log('The tracking swatch left view!', entry.target);
      if (!!timerRef && !!timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setTooltipShowingStatus(Object.fromEntries(keys.map((key) => [key, false])));
    }
  });

  const activeDescendant =
    options.length > 0 && focused >= 0 ? `${id}-${options[focused].code}-${options[focused].codeValue}` : '';

  let className = styles['graphic-option-listbox'];
  if (scrollable) className += ` ${styles['scrollable']}`;
  if (scrollable && graphicType === ThumbnailType.SCENE7_IMAGE) className += ` ${styles['scene7']}`;
  if (isInvalid) className += ` ${styles['invalid']}`;

  const handlePreviewOption = (index) => {
    if (previewable && !!onPreviewOption && typeof onPreviewOption === 'function') {
      onPreviewOption(index);
    }
  };

  const handleSelectOption = (option) => {
    if (!!onSelectOption && typeof onSelectOption === 'function') {
      onSelectOption(option);
    }
  };

  const handleBlur = () => {
    setFocused(-1);
    handlePreviewOption(hovered);
  };

  const handleFocus = () => {
    const selectedIndex = options.findIndex((option) => option.isSelected);
    const newFocused = selectedIndex >= 0 ? selectedIndex : hovered >= 0 ? hovered : 0;
    setFocused(newFocused);
    handlePreviewOption(hovered >= 0 ? hovered : newFocused);
  };

  const handleKeyDown = (e) => {
    if (options.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Home':
      case 'End':
        e.preventDefault();
        let newFocused;
        if (e.key === 'ArrowLeft') {
          newFocused = focused === 0 ? 0 : focused - 1;
        } else if (e.key === 'ArrowRight') {
          newFocused = focused === options.length - 1 ? options.length - 1 : focused + 1;
        } else if (e.key === 'Home') {
          newFocused = 0;
        } else {
          newFocused = options.length - 1;
        }
        setFocused(newFocused);
        handlePreviewOption(newFocused);
        break;
      case ' ':
        e.preventDefault();
        if (multiSelectable && !options[focused].isSoldOut) {
          handleSelectOption(options[focused]);
          handlePreviewOption(focused);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (!options[focused].isSoldOut) {
          handleSelectOption(options[focused]);
          handlePreviewOption(focused);
        }
        break;
      default:
        break;
    }
  };

  const handleMouseLeave = () => {
    const active = !!ref && !!ref.current && document.activeElement === ref.current;
    const selectedIndex = options.findIndex((option) => option.isSelected);
    if (active || selectedIndex >= 0) {
      setHovered(-1);
      handlePreviewOption(active ? focused : selectedIndex);
    }
  };

  const handleClickItem = (index) => {
    setFocused(index);
    if (isMobile) {
      const { code, codeValue } = options[index];
      const newKey = `${code}-${codeValue}`;

      if (!!timerRef && !!timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setTooltipShowingStatus(Object.fromEntries(keys.map((key) => [key, key === newKey])));
      timerRef.current = setTimeout(() => {
        setTooltipShowingStatus(Object.fromEntries(keys.map((key) => [key, false])));
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }, 2000);
    }
    handleSelectOption(options[index]);
  };

  const handleHoverOutItem = () => {
    if (scrollingIntoView) return;
    if (!showPreviewWhenHoveredOutSwatch) setHovered(-1);
    handlePreviewOption(
      showPreviewWhenHoveredOutSwatch
        ? hovered
        : !!ref && !!ref.current && document.activeElement === ref.current && focused >= 0
        ? focused
        : -1
    );
  };

  const handleHoverOverItem = (index) => {
    if (scrollingIntoView && hovered >= 0 && hovered !== index) return;
    setHovered(index);
    handlePreviewOption(index);
  };

  return (
    <div
      {...rest}
      aria-activedescendant={activeDescendant}
      aria-multiselectable={multiSelectable}
      aria-orientation="horizontal"
      className={className}
      id={id}
      ref={previewable ? ref : undefined}
      role="listbox"
      tabIndex="0"
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseLeave={showPreviewWhenHoveredOutSwatch ? handleMouseLeave : undefined}>
      {options.map((option, index) => {
        const { code, codeValue, isSoldOut } = option;
        const listboxItemId = `${id}-${code}-${codeValue}`;
        return (
          <GraphicOptionListboxItem
            id={listboxItemId}
            itemData={{
              focused: index === focused,
              graphicType,
              hovered: previewable && index === hovered,
              option,
              previewable,
            }}
            itemIndex={index}
            key={listboxItemId}
            ref={isMobile && tooltipShowingStatus[`${code}-${codeValue}`] ? trackingRef : undefined}
            showTooltip={isMobile ? tooltipShowingStatus[`${code}-${codeValue}`] : undefined}
            onClickItem={isSoldOut ? undefined : handleClickItem}
            onHoverOutItem={handleHoverOutItem}
            onHoverOverItem={handleHoverOverItem}
          />
        );
      })}
    </div>
  );
};

GraphicOptionListbox.propTypes = {
  graphicType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  id: PropTypes.string.isRequired,
  isInvalid: PropTypes.bool,
  multiSelectable: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      codeValue: PropTypes.string.isRequired,
      colorGroupList: PropTypes.arrayOf(PropTypes.string).isRequired,
      colorReference: PropTypes.string.isRequired,
      componentId: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      estimatedShipDate: PropTypes.string.isRequired,
      futureShipDateSettingStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
      helpLink: PropTypes.shape({
        helpText: PropTypes.string.isRequired,
        linkText: PropTypes.string.isRequired,
      }),
      inventoryMessage: PropTypes.string.isRequired,
      isFutureShipDateSettable: PropTypes.bool.isRequired,
      isOutOfStock: PropTypes.bool.isRequired,
      isSelected: PropTypes.bool.isRequired,
      isSoldOut: PropTypes.bool.isRequired,
      itemId: PropTypes.string.isRequired,
      optionText: PropTypes.string.isRequired,
      outOfStockMessage: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      questionId: PropTypes.number.isRequired,
      sequence: PropTypes.number.isRequired,
      specialBomCharge: PropTypes.string.isRequired,
      useTexture: PropTypes.bool.isRequired,
      yearOptionPricing: PropTypes.oneOf([0, 1, 2]).isRequired,
    })
  ).isRequired,
  previewable: PropTypes.bool,
  scrollable: PropTypes.bool,
  scrollingIntoView: PropTypes.bool,
  showPreviewWhenHoveredOutSwatch: PropTypes.bool,
  onPreviewOption: PropTypes.func,
  onSelectOption: PropTypes.func,
};

export default GraphicOptionListbox;
